// import '../App.css';
import '../css/chat.css';
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-hot-toast';

function Chat() {
  const { handle } = useParams(); // using handle from route params
  const [companyInfo, setCompanyInfo] = useState(null);
  const [companyId, setCompanyId] = useState(null); // store company id from fetched data
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState([]);
  const [sessionId] = useState(Date.now().toString());
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(true);

  // Review state
  const [reviewComment, setReviewComment] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  const historyEndRef = useRef(null);



  const chatHistoryRef = useRef(null);


  // Scroll to bottom (for user messages)
  const scrollToBottom = () => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  };

  const scrollToLastBotMessage = () => {
    if (!chatHistoryRef.current) return;
  
    const botMessages = chatHistoryRef.current.querySelectorAll('.chat-message-bot');
    if (botMessages.length > 0) {
      const lastBotMsg = botMessages[botMessages.length - 1];
      const container = chatHistoryRef.current;
  
      // Position of bot message relative to container
      const relativeTop = lastBotMsg.offsetTop - container.offsetTop;
  
      // Add padding so the user sees the start + ~5 lines
      const lineHeight = 20; // adjust based on your CSS
      const extraLines = 5;
      const offset = lineHeight * extraLines;
  
      container.scrollTo({
        top: relativeTop - offset,
        behavior: 'smooth',
      });
    }
  };
  


  useEffect(() => {
    if (history.length === 0) return;
  
    const lastMsg = history[history.length - 1];
    if (lastMsg.sender === "user") {
      scrollToBottom(); // user → scroll to bottom
    } else if (lastMsg.sender === "bot") {
      scrollToLastBotMessage(); // bot → scroll to top of response
    }
  }, [history]);

  // Helper function to get time-based greeting
  function getTimeOfDayGreeting(companyName) {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return `Good morning! Welcome to ${companyName}. How can I assist you today?`;
    } else if (hour >= 12 && hour < 18) {
      return `Good afternoon! Welcome to ${companyName}. How can I assist you today?`;
    } else if (hour >= 18 && hour < 22) {
      return `Good evening! Welcome to ${companyName}. How can I assist you today?`;
    } else {
      return `Hello! Welcome to ${companyName}. How can I assist you today?`;
    }
  }

  
  useEffect(() => {
    async function fetchCompanyInfo() {
      try {

        // 🔑 Extract subdomain from hostname
        const hostname = window.location.hostname; // sweetsensation.aboutme.retreeval.com
        const parts = hostname.split(".");
        let subdomain = parts[0];

        // If you're still supporting the /chat/{handle} route as fallback:
        const effectiveHandle = handle || subdomain;

        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/organization/${effectiveHandle}`
        );

        
        // const res = await axios.get(`http://localhost:8000/organization/${handle}`);
        setCompanyInfo(res.data);
        setCompanyId(res.data.id);
        setIsTyping(true);
        setHistory([]); // Clear previous history immediately
  

        // Build greeting message with bold company name using markdown
        const companyName = res.data.name || "Company"; // fallback
        const companyNameUpper = companyName.toUpperCase(); // make it all caps

        const rawGreeting = res.data.greeting_message?.trim() || getTimeOfDayGreeting(companyName);
        const greetingMsg = rawGreeting.replace(companyName, companyNameUpper);


        // Show greeting after 1.5 seconds delay
        setTimeout(() => {
          setHistory([{ sender: 'bot', text: greetingMsg }]);
          setIsTyping(false);
        }, 3500); // Actually a 1.5 secs delay, but the rendering takes 2 secs so 
  
      } catch (error) {
        console.error('Failed to fetch company info', error);
      } finally {
        setIsLoadingCompany(false);
      }
    }
  
    fetchCompanyInfo();
  }, [handle]);
  


  useEffect(() => {
    const timer = setTimeout(() => setIsPageLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleQuestionChange = (e) => setQuestion(e.target.value);

  const handleAskQuestion = async () => {
    if (!question.trim() || !companyId || isTyping) return;  // prevent if already typing

    const userMessage = { sender: 'user', text: question };
    setHistory((prev) => [...prev, userMessage]);
    setQuestion('');
    setIsTyping(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/ask/`, {
        session_id: sessionId,
        question,
        company_id: companyId,  // send companyId here, not handle
      });

      const { answer } = response.data;
      const botMessage = { sender: 'bot', text: answer };
      setHistory((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Error asking question:', error);
      const errorMessage = { sender: 'bot', text: 'Sorry, something went wrong.' };
      setHistory((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAskQuestion();
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!companyId) return;

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/review/`, {
        organization_id: companyId, // use companyId here
        rating: reviewRating,
        comment: reviewComment,
        session_id: sessionId,
      });
      setReviewSubmitted(true);
      toast.success('Review Submitted');
    } catch (error) {
      // console.error('Error submitting review:', error);
      toast.error('Failed to submit review. Try again.');
      // alert();
    }
  };

  const formatUrl = (url) => {
    if (!url) return url;
    return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
  };

  if (isPageLoading || isLoadingCompany || !companyInfo) {
    return (
      <div className="chat-loading">
        <div className="typing-dots-loader"><span></span><span></span><span></span></div>
        <p>Loading...</p>
      </div>
    );
  }

  const { name, logo_url, website, twitter, instagram, tiktok, whatsapp, contact_email, phone } = companyInfo;

  const socials = [
    { label: "Website", value: website },
    { label: "Twitter", value: twitter },
    { label: "Instagram", value: instagram },
    { label: "Tiktok", value: tiktok },
    { label: "WhatsApp", value: whatsapp },
    { label: "Email", value: contact_email },
    { label: "Phone", value: phone }
  ];

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-header-left">
          {logo_url && <img src={logo_url} alt="Logo" className="chat-logo" />}
          <h1 className="chat-title">{name}</h1>
        </div>
        <div className="chat-header-right">
          {socials.map(({ label, value }) =>
            value ? (
              <a
                key={label}
                href={
                  label === "Email" ? `mailto:${value}` :
                  label === "Phone" ? `tel:${value}` :
                  formatUrl(value)
                }
                target="_blank"
                rel="noopener noreferrer"
                className="chat-social-link"
              >
                {label}
              </a>
            ) : null
          )}
        </div>
      </div>

      <div className="chat-box">
        <div className="chat-history" ref={chatHistoryRef}>
          {history.map((entry, index) => (
            <div
              key={index}
              className={`chat-message ${entry.sender === 'user' ? 'chat-message-user' : 'chat-message-bot'}`}
            >
              {entry.sender === 'user' ? entry.text : <ReactMarkdown>{entry.text}</ReactMarkdown>}
            </div>
          ))}
          {isTyping && <div className="typing-indicator">{name} is typing...</div>}
          <div ref={historyEndRef} />
        </div>

        <form
          className="chat-input-area"
          onSubmit={(e) => {
            e.preventDefault();
            handleAskQuestion();
          }}
        >
          <input
            type="text"
            value={question}
            onChange={handleQuestionChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask your question..."
            className="chat-input"
            autoComplete="off"
            // disabled={isTyping}  // disable input while typing
          />
          <button
            type="submit"
            className="chat-input-button"
            disabled={isTyping || !question.trim()}  // disable button while typing or empty question
          >
            Ask
          </button>
        </form>
      </div>

      {/* Review Section */}
      <div className="review-section">
        <h3>Rate Your Chat Experience</h3>
        {reviewSubmitted ? (
          <p className="review-thanks">Thank you for your feedback!</p>
        ) : (
          <form onSubmit={handleReviewSubmit} className="review-form">
            <label>
              Rating:
              <select value={reviewRating} onChange={(e) => setReviewRating(parseInt(e.target.value))}>
                {[5, 4, 3, 2, 1].map((num) => (
                  <option key={num} value={num}>{num} ⭐</option>
                ))}
              </select>
            </label>
            <textarea
              placeholder="Optional comment..."
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              rows={3}
              className="review-textarea"
            />
            <button type="submit" className="review-submit-button">Submit Review</button>
          </form>
        )}
      </div>
    </div>
  );
}

export default Chat;

